/**
 * browser-auth-lock
 *
 * Coordinates Supabase browser auth refreshes without relying on fragile
 * navigator.locks behavior.
 *
 * Primary responsibilities:
 * - Provide a Supabase auth lock for browsers where Web Locks are missing
 * - Also take over when Web Locks exist but throw (Safari UnavailableError)
 * - Serialize refresh-token rotation across local browser contexts
 *
 * Dependencies:
 * - Browser localStorage
 * - @supabase/supabase-js (LockAcquireTimeoutError subclass for GoTrue)
 *
 * Why we do not prefer navigator.locks even when present:
 * Safari advertises navigator.locks but can throw DOMException
 * `UnavailableError` during auth refresh. auth-js ≤2.71 defaults to
 * navigatorLock when `auth.lock` is unset, which surfaces as an unhandled
 * UnavailableError on /chat. Our storage/process lock avoids that path.
 * Callers must resolve this lock at client-create time (not module load)
 * so SSR never freezes an unset lock into the browser client.
 *
 * Timeout errors MUST be `instanceof LockAcquireTimeoutError` (we throw
 * NavigatorLockAcquireTimeoutError, which subclasses it). GoTrue's
 * auto-refresh tick catches that class and soft-skips; a plain Error
 * becomes an unhandled Next.js overlay during login.
 */

import { NavigatorLockAcquireTimeoutError } from '@supabase/supabase-js'

type BrowserAuthLock = <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
) => Promise<R>

type BrowserAuthLockRecord = {
  expiresAt: number
  owner: string
}

const LOCK_KEY_PREFIX = 'losi:supabase-auth-lock:'
const LOCK_STALE_MS = 10_000
const LOCK_HEARTBEAT_MS = 3_000
const LOCK_POLL_MS = 50
const LOCK_VERIFY_MS = 25

const processLocks = new Map<string, Promise<unknown>>()

// --------------

function getStorage() {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const testKey = `${LOCK_KEY_PREFIX}test`
    window.localStorage.setItem(testKey, '1')
    window.localStorage.removeItem(testKey)
    return window.localStorage
  } catch {
    return null
  }
}

// --------------

function createOwnerId() {
  const randomId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)

  return `${Date.now()}:${randomId}`
}

// --------------

function wait(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

// --------------

function createAcquireTimeoutError(name: string) {
  // Must be instanceof LockAcquireTimeoutError so GoTrue soft-skips the
  // auto-refresh tick instead of rethrowing into the Next.js error overlay.
  return new NavigatorLockAcquireTimeoutError(
    `Could not acquire Supabase auth lock "${name}" before timeout`
  )
}

// --------------

function readLockRecord(storage: Storage, key: string) {
  try {
    const rawRecord = storage.getItem(key)
    if (!rawRecord) {
      return null
    }

    const record = JSON.parse(rawRecord) as Partial<BrowserAuthLockRecord>
    if (
      typeof record.owner !== 'string' ||
      typeof record.expiresAt !== 'number'
    ) {
      return null
    }

    return record as BrowserAuthLockRecord
  } catch {
    return null
  }
}

// --------------

function writeLockRecord(storage: Storage, key: string, owner: string) {
  storage.setItem(
    key,
    JSON.stringify({
      expiresAt: Date.now() + LOCK_STALE_MS,
      owner
    } satisfies BrowserAuthLockRecord)
  )
}

// --------------

async function tryAcquireStorageLock(
  storage: Storage,
  key: string,
  owner: string
) {
  const currentRecord = readLockRecord(storage, key)
  const now = Date.now()

  if (
    currentRecord &&
    currentRecord.owner !== owner &&
    currentRecord.expiresAt > now
  ) {
    return false
  }

  writeLockRecord(storage, key, owner)
  await wait(LOCK_VERIFY_MS + Math.floor(Math.random() * LOCK_VERIFY_MS))

  return readLockRecord(storage, key)?.owner === owner
}

// --------------

function refreshStorageLock(storage: Storage, key: string, owner: string) {
  if (readLockRecord(storage, key)?.owner !== owner) {
    return
  }

  writeLockRecord(storage, key, owner)
}

// --------------

function releaseStorageLock(storage: Storage, key: string, owner: string) {
  if (readLockRecord(storage, key)?.owner === owner) {
    storage.removeItem(key)
  }
}

// --------------

async function forceAcquireStorageLock(
  storage: Storage,
  key: string,
  owner: string
) {
  // Steal a stuck cross-tab lock after wait timeout so a dead tab / hung
  // refresh cannot permanently block login.
  storage.removeItem(key)
  writeLockRecord(storage, key, owner)
  await wait(LOCK_VERIFY_MS)
  return readLockRecord(storage, key)?.owner === owner
}

async function acquireStorageLock<R>(
  storage: Storage,
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
) {
  const key = `${LOCK_KEY_PREFIX}${name}`
  const owner = createOwnerId()
  const startedAt = Date.now()

  while (!(await tryAcquireStorageLock(storage, key, owner))) {
    if (acquireTimeout >= 0 && Date.now() - startedAt >= acquireTimeout) {
      if (acquireTimeout === 0) {
        throw createAcquireTimeoutError(name)
      }

      const stole = await forceAcquireStorageLock(storage, key, owner)
      if (!stole) {
        throw createAcquireTimeoutError(name)
      }
      break
    }

    await wait(LOCK_POLL_MS + Math.floor(Math.random() * LOCK_POLL_MS))
  }

  const heartbeat = window.setInterval(
    () => refreshStorageLock(storage, key, owner),
    LOCK_HEARTBEAT_MS
  )

  try {
    return await fn()
  } finally {
    window.clearInterval(heartbeat)
    releaseStorageLock(storage, key, owner)
  }
}

// --------------

function acquireProcessLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
) {
  const previousLock = processLocks.get(name)

  if (previousLock && acquireTimeout === 0) {
    return Promise.reject(createAcquireTimeoutError(name))
  }

  const queuedAfter = previousLock ?? Promise.resolve()

  const run = (async () => {
    if (previousLock && acquireTimeout > 0) {
      let timeoutId: number | undefined
      try {
        await Promise.race([
          queuedAfter.catch(() => undefined),
          new Promise<never>((_, reject) => {
            timeoutId = window.setTimeout(() => {
              reject(createAcquireTimeoutError(name))
            }, acquireTimeout)
          })
        ])
      } finally {
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId)
        }
      }
    } else if (previousLock) {
      await queuedAfter.catch(() => undefined)
    }

    return fn()
  })()

  const storedLock = run.catch(() => undefined)

  processLocks.set(name, storedLock)

  void storedLock.finally(() => {
    if (processLocks.get(name) === storedLock) {
      processLocks.delete(name)
    }
  })

  return run
}

// --------------

const fallbackBrowserAuthLock: BrowserAuthLock = async (
  name,
  acquireTimeout,
  fn
) => {
  const storage = getStorage()

  if (!storage) {
    return acquireProcessLock(name, acquireTimeout, fn)
  }

  return acquireProcessLock(name, acquireTimeout, () =>
    acquireStorageLock(storage, name, acquireTimeout, fn)
  )
}

// --------------

export function getBrowserAuthLock(): BrowserAuthLock | undefined {
  if (typeof window === 'undefined') {
    return undefined
  }

  // Always install our lock in the browser. Preferring native locks when
  // `navigator.locks` exists caused consistent Safari UnavailableError noise
  // during token refresh; the localStorage-backed lock is cross-tab safe.
  return fallbackBrowserAuthLock
}

// --------------

export function resetBrowserAuthLockForTests() {
  processLocks.clear()
}
